import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  Megaphone,
  Pencil,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "src/components/ui/ConfirmDialog";
import type { ActivityActor } from "src/lib/activityService";
import { getBroadcastEnvInfo } from "src/lib/notificationRoutes";
import {
  archivePortalNotification,
  publishPortalNotification,
  toMillis,
} from "src/lib/portalNotificationService";
import {
  formatPortalNotificationStatus,
  formatWindow,
  hasPublicationWindow,
  usePortalNotificationsStore,
} from "src/store/portalNotificationsStore";
import type { PortalNotification } from "src/types/portalNotifications";
import { portalCategoryFromProto } from "src/types/portalNotifications";

interface Props {
  canWrite: boolean;
  actor: ActivityActor | null;
  /** Opens the composer to edit an existing draft. */
  onEdit: (n: PortalNotification) => void;
  /** Opens the composer prefilled as a new draft. */
  onDuplicate: (n: PortalNotification) => void;
}

const STATUS_TABS = [
  { label: "All", value: null },
  { label: "Draft", value: "DRAFT" },
  { label: "Published", value: "PUBLISHED" },
  { label: "Archived", value: "ARCHIVED" },
] as const;

const STATUS_CHIP: Record<string, string> = {
  DRAFT: "bg-amber-50 text-amber-700 border-amber-200",
  PUBLISHED: "bg-[#E8F5E4] text-[#4F9A53] border-[#6CBF64]",
  ARCHIVED: "bg-[#F4F4F5] text-[#777777] border-[#E5E5E5]",
};

export const PortalNotificationsList: React.FC<Props> = ({
  canWrite,
  actor,
  onEdit,
  onDuplicate,
}) => {
  const {
    env,
    statusFilter,
    items,
    pageNumber,
    totalPages,
    totalElements,
    loading,
    error,
    setStatusFilter,
    setPage,
    load,
  } = usePortalNotificationsStore();
  const envInfo = getBroadcastEnvInfo(env);

  const [pendingAction, setPendingAction] = useState<{
    kind: "publish" | "archive";
    item: PortalNotification;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAction = async () => {
    if (!pendingAction) return;
    const { kind, item } = pendingAction;
    setActionBusy(true);
    setActionError(null);
    try {
      if (kind === "publish") {
        await publishPortalNotification(env, item.id, item.headline, actor);
      } else {
        await archivePortalNotification(env, item.id, item.headline, actor);
      }
      setPendingAction(null);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : `${kind} failed`);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[1.5rem] border border-[#E5E5E5] bg-white">
      {/* Header: status filters */}
      <div className="shrink-0 border-b border-[#E5E5E5] px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-bold text-[#1F1F1F] sm:text-lg">
            Notifications
          </h2>
          <span className="rounded-full border border-[#E5E5E5] bg-[#F9FAFB] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
            {envInfo.label}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {STATUS_TABS.map((tab) => {
            const active = statusFilter === tab.value;
            return (
              <button
                key={tab.label}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
                  active
                    ? "border-[#6CBF64] bg-[#E8F5E4] text-[#4F9A53]"
                    : "border-[#E5E5E5] bg-white text-[#777777] hover:border-[#D0D0D0]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body — only this region scrolls */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3 sm:px-5">
        {error && (
          <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
            {error}
          </p>
        )}
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[#777777]">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[#E5E5E5] px-6 py-10 text-center text-sm text-[#777777]">
            No notifications on {envInfo.label} yet.
          </p>
        ) : (
          <ul className="divide-y-2 divide-[#F4F4F5]">
            {items.map((n) => {
              const status = formatPortalNotificationStatus(n);
              const windowLabel = formatWindow(n);
              const canEditRow = canWrite && status === "DRAFT";
              // Publish needs a window that has not already ended. An unset
              // window, or one whose end is in the past, stays a draft.
              const canPublishRow =
                canWrite &&
                status === "DRAFT" &&
                hasPublicationWindow(n) &&
                toMillis(n.endAtUnixMillis) > Date.now();
              const canArchiveRow = canWrite && status !== "ARCHIVED";
              return (
                <li key={n.id} className="py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_CHIP[status]}`}
                        >
                          {status}
                        </span>
                        <span className="rounded-full bg-[#E8EBF5] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2C3454]">
                          {portalCategoryFromProto(n.category)}
                        </span>
                        {windowLabel ? (
                          <span className="text-[11px] text-[#9CA3AF]">
                            {windowLabel}
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate text-sm font-bold text-[#1F1F1F]">
                        {n.headline}
                      </p>
                      <p className="mt-0.5 line-clamp-1 text-xs text-[#6B7280]">
                        {n.body}
                      </p>
                      {n.targeting.endsWith("SPECIFIC_COMPANIES") && (
                        <p className="mt-0.5 text-[11px] font-semibold text-[#9CA3AF]">
                          Targeted: {n.targetCompanyIds.length} portal
                          {n.targetCompanyIds.length === 1 ? "" : "s"}
                        </p>
                      )}
                    </div>
                    {canWrite && (
                      <div className="flex shrink-0 items-center gap-1">
                        {canEditRow && (
                          <button
                            type="button"
                            title="Edit draft"
                            onClick={() => onEdit(n)}
                            className="rounded-lg border border-[#E5E5E5] p-1.5 text-[#777777] hover:border-[#6CBF64] hover:text-[#4F9A53]"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Duplicate"
                          onClick={() => onDuplicate(n)}
                          className="rounded-lg border border-[#E5E5E5] p-1.5 text-[#777777] hover:border-[#6CBF64] hover:text-[#4F9A53]"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        {canPublishRow && (
                          <button
                            type="button"
                            title="Publish"
                            onClick={() =>
                              setPendingAction({ kind: "publish", item: n })
                            }
                            className="rounded-lg border border-[#6CBF64] bg-[#E8F5E4] p-1.5 text-[#4F9A53] hover:border-[#6CBF64]"
                          >
                            <Megaphone className="h-4 w-4" />
                          </button>
                        )}
                        {canArchiveRow && (
                          <button
                            type="button"
                            title="Archive"
                            onClick={() =>
                              setPendingAction({ kind: "archive", item: n })
                            }
                            className="rounded-lg border border-[#E5E5E5] p-1.5 text-[#777777] hover:border-rose-300 hover:text-rose-600"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {status === "PUBLISHED" &&
                    toMillis(n.publishedAtUnixMillis) > 0 && (
                      <p className="mt-1 text-[11px] text-[#9CA3AF]">
                        Published{" "}
                        {new Date(
                          toMillis(n.publishedAtUnixMillis),
                        ).toLocaleString()}
                      </p>
                    )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-between border-t border-[#E5E5E5] px-4 py-3 sm:px-5">
          <p className="text-xs text-[#777777]">
            Page {pageNumber} of {totalPages} · {totalElements} total
          </p>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={pageNumber <= 1 || loading}
              onClick={() => setPage(pageNumber - 1)}
              className="rounded-lg border border-[#E5E5E5] p-1.5 text-[#777777] disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={pageNumber >= totalPages || loading}
              onClick={() => setPage(pageNumber + 1)}
              className="rounded-lg border border-[#E5E5E5] p-1.5 text-[#777777] disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingAction != null}
        title={
          pendingAction?.kind === "publish"
            ? "Publish this notification?"
            : "Archive this notification?"
        }
        message={
          pendingAction?.kind === "publish" ? (
            <>
              Publishes{" "}
              <span className="font-bold">
                &ldquo;{pendingAction?.item.headline}&rdquo;
              </span>{" "}
              on {envInfo.label}. Eligible portal users will see it as a login
              pop-up during its scheduled window.
            </>
          ) : (
            <>
              Archives{" "}
              <span className="font-bold">
                &ldquo;{pendingAction?.item.headline}&rdquo;
              </span>
              . It will stop appearing in the pop-up and Hub.
            </>
          )
        }
        confirmLabel={pendingAction?.kind === "publish" ? "Publish" : "Archive"}
        destructive={pendingAction?.kind === "archive"}
        busy={actionBusy}
        onConfirm={() => void runAction()}
        onCancel={() => {
          if (!actionBusy) {
            setPendingAction(null);
            setActionError(null);
          }
        }}
      />
      {actionError && (
        <p className="mx-4 mb-3 shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 sm:mx-5">
          {actionError}
        </p>
      )}
    </section>
  );
};

export default PortalNotificationsList;
